import { Tinkoff } from "components";
import { loadTinkoff, paymentFlowTypes, resolvePaymentFlow } from "helpers";
import {
  finalPayPrice,
  getManualAdjustment,
  getManualAdjustmentLabel,
  getPercentDiscountLines,
  getPromoDiscountAmount,
  getPromoDiscountLabel,
  hasAdjustments,
  hasRooms,
  hasServices,
  paidSum,
  roomsTotal,
  servicesTotal,
  tariffTotal,
} from "helpers/orderPricing";
import { localePrice } from "helpers/locales";

import { setIsModal, setMessage } from "features/appSlice";
import { useDispatch } from "react-redux";

const Summary = ({ order }) => {
  const isSuccess = order.status !== "Не оплачено";

  const dispatch = useDispatch();

  const roomsSum = roomsTotal(order);
  const servicesSum = servicesTotal(order);
  const tariffSum = tariffTotal(order);
  const paySum = finalPayPrice(order);
  const paidAmount = paidSum(order);
  const promoDiscount = getPromoDiscountAmount(order);
  const percentDiscountLines = getPercentDiscountLines(order);
  const manualAdjustment = getManualAdjustment(order);
  const showRoomsSubtotal =
    hasRooms(order) && (order.suits.length > 1 || hasServices(order));
  const showServicesSubtotal =
    hasServices(order) && order.services.length > 1;

  const checkoutSubmit = (e, paymentPayload) => {
    e.preventDefault();
    const paymentFlow = resolvePaymentFlow(paymentPayload);

    if (paymentFlow.type === paymentFlowTypes.ERROR) {
      console.error("[payment] invalid invoice payment payload", paymentPayload);
      dispatch(
        setMessage({
          type: "error",
          msg: paymentFlow.reason || "Ошибка оплаты, попробуйте позже",
        })
      );
      return false;
    }

    if (paymentFlow.type === paymentFlowTypes.REDIRECT) {
      window.location.assign(paymentFlow.payUrl);
      return false;
    }

    loadTinkoff(() => {
      window.pay(e.target);
    });
    return false;
  };

  return (
    <section className="summary-container" aria-label="Сводка бронирования">
      <div className="summary-card card">
        <div className="summary-event-group">
          <h6>{order.header}</h6>
          <div className="pricing-summary">
            {hasRooms(order) && (
              <>
                <div className="pricing-section-title">Проживание</div>
                <ul className="summary-table">
                  {order.suits.map((item) => (
                    <li key={item.id}>
                      <p>{item.name}</p>
                      <span className="summary-price">
                        {localePrice(item.price)}
                      </span>
                    </li>
                  ))}
                </ul>
                {showRoomsSubtotal && (
                  <div className="pricing-row pricing-row--subtotal">
                    <span>Итого за проживание</span>
                    <span className="summary-price">
                      {localePrice(roomsSum)}
                    </span>
                  </div>
                )}
              </>
            )}

            {hasServices(order) && (
              <>
                {hasRooms(order) && <div className="pricing-divider" />}
                <div className="pricing-section-title">Услуги</div>
                <ul className="summary-table">
                  {order.services.map((item) => (
                    <li key={item.id}>
                      <p>
                        {item.name}
                        <small className="pricing-meta">
                          {localePrice(item.unit_price)} × {item.quantity}
                        </small>
                      </p>
                      <span className="summary-price">
                        {localePrice(item.total_price)}
                      </span>
                    </li>
                  ))}
                </ul>
                {showServicesSubtotal && (
                  <div className="pricing-row pricing-row--subtotal">
                    <span>Итого за услуги</span>
                    <span className="summary-price">
                      {localePrice(servicesSum)}
                    </span>
                  </div>
                )}
              </>
            )}

            <div className="pricing-divider" />
            <div className="pricing-row pricing-row--subtotal">
              <span>Итого по тарифу</span>
              <span className="summary-price">{localePrice(tariffSum)}</span>
            </div>

            {hasAdjustments(order) && (
              <>
                <div className="pricing-divider" />
                <div className="pricing-section-title">Скидки и корректировки</div>
                <ul className="discount-list discount-list--open">
                  {promoDiscount > 0 && (
                    <li>
                      <small>{getPromoDiscountLabel(order)}</small>
                      <span className="summary-price discount-list__amount">
                        −{localePrice(promoDiscount)}
                      </span>
                    </li>
                  )}
                  {percentDiscountLines.map((line, index) => (
                    <li key={`${line.text}-${index}`}>
                      <small>{line.text}</small>
                      <span className="summary-price discount-list__amount">
                        −{localePrice(line.amount)}
                      </span>
                    </li>
                  ))}
                  {manualAdjustment > 0 && (
                    <li>
                      <small>{getManualAdjustmentLabel(order)}</small>
                      <span className="summary-price discount-list__amount">
                        −{localePrice(manualAdjustment)}
                      </span>
                    </li>
                  )}
                </ul>
              </>
            )}

            <div className="pricing-divider" />
            <div className="pricing-row pricing-row--total">
              <span>К оплате</span>
              <span className="summary-price">{localePrice(paySum)}</span>
            </div>
          </div>

          <div className="summary-subtotal">
            {order.bonus.applied && (
              <div className="summary-subtotal-row">
                <span className="summary-price paid">
                  <small>Оплачено бонусами:</small>
                  {localePrice(order.bonus.bonus_point_to_use)}
                </span>
              </div>
            )}
            <div className="summary-subtotal-row">
              <span
                className={
                  isSuccess
                    ? "summary-price paid"
                    : "summary-price paid failure"
                }
              >
                <small>Оплачено:</small>
                {paidAmount ? localePrice(paidAmount) : "0 ₽"}
              </span>
            </div>
            {paidAmount > paySum && paySum > 0 && (
              <div className="summary-subtotal-row">
                <span className="summary-price failure">
                  <small>Переплата:</small>
                  {localePrice(paidAmount - paySum)}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="summary-invoices">
          <h5>Счета</h5>
          <ul className="summary-invoices-list">
            {order.invoices.map((item) => {
              const isCashPayment =
                item.payment_method?.trim() === "Наличными";
              const isNotPaid = !item.pay;
              return (
                <li key={item.id} className="invoice-card">
                  <div className="invoice-header">
                    <p className="invoice-name">{item.name}</p>
                    <span className="invoice-price">
                      {localePrice(item.price)}
                    </span>
                  </div>
                  <div className="invoice-info">
                    <div className="invoice-meta">
                      <span className="invoice-meta-item">
                        <small>Тип оплаты:</small>
                        <span className="payment-method-badge">
                          {item.payment_method || "—"}
                        </span>
                      </span>
                      <span className="invoice-meta-item">
                        <small>Статус:</small>
                        <span className="payment-status">
                          <span className="payment-status-label">
                            {item.pay ? "Оплачено" : "Не оплачено"}
                          </span>
                          <span
                            className={`payment-status-icon ${
                              item.pay ? "success" : "failure"
                            }`}
                          ></span>
                        </span>
                      </span>
                    </div>
                  </div>
                  {isNotPaid && (
                    <div className="invoice-actions">
                      {isCashPayment ? (
                        <div className="cash-payment-message">
                          <small>
                            Свяжитесь с менеджером для оплаты наличными
                          </small>
                          <br />
                          <small>
                            Оплата принимается в Space Химки. г. Химки, ул.
                            Кирова, с24
                          </small>
                        </div>
                      ) : (
                        <>
                          <form
                            name="TinkoffPayForm"
                            onSubmit={(e) =>
                              checkoutSubmit(e, item.payment_data)
                            }
                          >
                            <Tinkoff values={item.payment_data} />
                            <button
                              className="btn btn-primary small"
                              type="submit"
                              value="Оплатить"
                            >
                              Оплатить
                            </button>
                          </form>
                          <div className="balance-container">
                            <button
                              className="btn-balance"
                              onClick={() =>
                                dispatch(
                                  setIsModal({ modalName: "balance", ...item })
                                )
                              }
                            >
                              Оплатить с личного счета
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default Summary;
